// src/hooks/useTableFilters.ts
import { useState, useReducer } from 'react';
import { ColumnFiltersState } from '@tanstack/react-table';
import { getUniqueValuesForColumn } from '@/utils/table/tableUtils';

// Types for our filter state
interface FilterDialogState {
  isOpen: boolean;
  options: string[];
  selected: string[];
  search: string;
  // Add mapping between display values and actual values
  valueMapping?: Record<string, string>;
}

interface FilterState {
  [key: string]: FilterDialogState;
}

// Action types
type FilterAction =
  | {
      type: 'OPEN_FILTER';
      columnId: string;
      options: string[];
      valueMapping?: Record<string, string>;
    }
  | { type: 'CLOSE_FILTER'; columnId: string }
  | { type: 'SET_SEARCH'; columnId: string; search: string }
  | { type: 'TOGGLE_OPTION'; columnId: string; option: string }
  | { type: 'CLEAR_FILTER'; columnId: string };

// Initial state for a filter dialog
const initialFilterState: FilterDialogState = {
  isOpen: false,
  options: [],
  selected: [],
  search: '',
  valueMapping: undefined,
};

// Reducer function
function filterReducer(state: FilterState, action: FilterAction): FilterState {
  switch (action.type) {
    case 'OPEN_FILTER':
      return {
        ...state,
        [action.columnId]: {
          ...(state[action.columnId] || initialFilterState),
          isOpen: true,
          options: action.options,
          valueMapping: action.valueMapping,
        },
      };

    case 'CLOSE_FILTER':
      return {
        ...state,
        [action.columnId]: {
          ...state[action.columnId],
          isOpen: false,
        },
      };

    case 'SET_SEARCH':
      return {
        ...state,
        [action.columnId]: {
          ...state[action.columnId],
          search: action.search,
        },
      };

    case 'TOGGLE_OPTION': {
      const currentFilter = state[action.columnId];
      const newSelected = currentFilter.selected.includes(action.option)
        ? currentFilter.selected.filter((opt) => opt !== action.option)
        : [...currentFilter.selected, action.option];

      return {
        ...state,
        [action.columnId]: {
          ...currentFilter,
          selected: newSelected,
        },
      };
    }
    case 'CLEAR_FILTER':
      return {
        ...state,
        [action.columnId]: {
          ...state[action.columnId],
          selected: [],
          search: '',
        },
      };

    default:
      return state;
  }
}

export const useTableFilters = <T extends object>(
  data: T[],
  columnConfigs?: Array<{
    id: string;
    getOptionLabel?: (option: string) => string;
    filterOptions?: (options: string[], searchTerm: string) => string[];
  }>
) => {
  // State for the filter dialogs
  const [filterState, dispatch] = useReducer(filterReducer, {});

  // State for the table's column filters
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  // Helper functions
  const openFilter = (columnId: string, currentFilters: ColumnFiltersState) => {
    const rawOptions = getUniqueValuesForColumn(columnId, currentFilters, data);

    // Find the column config to get the getOptionLabel function
    const columnConfig = columnConfigs?.find((config) => config.id === columnId);

    let options = rawOptions;
    let valueMapping: Record<string, string> | undefined;

    // If we have a getOptionLabel function, create a mapping and use translated options
    if (columnConfig?.getOptionLabel) {
      valueMapping = {};
      options = rawOptions.map((option) => {
        const translatedOption = columnConfig.getOptionLabel!(option);
        valueMapping![translatedOption] = option;
        return translatedOption;
      });
    }

    dispatch({ type: 'OPEN_FILTER', columnId, options, valueMapping });
  };

  const closeFilter = (columnId: string) => {
    dispatch({ type: 'CLOSE_FILTER', columnId });
  };

  const setSearch = (columnId: string, search: string) => {
    dispatch({ type: 'SET_SEARCH', columnId, search });
  };

  const toggleOption = (columnId: string, option: string) => {
    dispatch({ type: 'TOGGLE_OPTION', columnId, option });
  };

  const clearFilter = (columnId: string) => {
    dispatch({ type: 'CLEAR_FILTER', columnId });
  };

  const applyFilter = (columnId: string) => {
    const filterStateForColumn = filterState[columnId];
    const selected = filterStateForColumn?.selected || [];
    const valueMapping = filterStateForColumn?.valueMapping;

    setColumnFilters((prev) => {
      const newFilters = prev.filter((f) => f.id !== columnId);
      if (selected.length > 0) {
        // If we have a value mapping, use the actual values for filtering
        const actualValues = valueMapping
          ? selected.map((displayValue) => valueMapping[displayValue])
          : selected;
        newFilters.push({ id: columnId, value: actualValues });
      }
      return newFilters;
    });
    closeFilter(columnId);
  };

  return {
    // State
    filterState,
    columnFilters,

    // Actions
    openFilter,
    closeFilter,
    setSearch,
    toggleOption,
    clearFilter,
    applyFilter,

    // Helper getters
    getFilterState: (columnId: string) => filterState[columnId] || initialFilterState,
    getFilteredOptions: (columnId: string) => {
      const state = filterState[columnId];
      if (!state) return [];

      // Find the column config to get the custom filter function
      const columnConfig = columnConfigs?.find((config) => config.id === columnId);

      // Use custom filter function if available, otherwise use default text filtering
      if (columnConfig?.filterOptions) {
        return columnConfig.filterOptions(state.options, state.search);
      }

      // Default text filtering
      return state.options.filter((opt) => opt.toLowerCase().includes(state.search.toLowerCase()));
    },
  };
};
